// Tech Pack section 2, "CAD & technical drawings" (replaces "Sketches"): technical flats, detail views with numbered
// callout pins, colourway CADs, placement drawings, the points-of-measure diagram and pattern / marker files.
// Design and inspiration sketches belong in R&D, not here. Replacing a drawing keeps the earlier file in its history;
// a sealed Tech Pack revision keeps the drawings it had, because files are never deleted and the revision stores them.

export type CadKind = "flat" | "detail" | "colourway" | "placement" | "pom" | "pattern";
export const CAD_KINDS: Array<[CadKind, string, string]> = [
  ["flat", "Technical flats", "Front and back are required before the Tech Pack goes for approval. Side and inside views are optional."],
  ["detail", "Detail views", "Close-ups of pockets, collars, cuffs and closures, with numbered pins."],
  ["colourway", "Colourway CADs", "One coloured flat per colourway, labelled from the Colourways section."],
  ["placement", "Placement drawings", "Where logos, embroidery, reflective tape, labels and transfers go, measured in mm from a reference point."],
  ["pom", "Measurement points (POM) diagram", "Point codes that match the Measurements section."],
  ["pattern", "Pattern & marker files", "Attachments only (DXF-AAMA / ASTM, Gerber, Lectra, PLT, PDF): downloaded, not previewed."],
];
export const CAD_KIND_LABEL = Object.fromEntries(CAD_KINDS.map(([k, l]) => [k, l])) as Record<CadKind, string>;
export const FLAT_VIEWS: Array<[string, string]> = [["front", "Front"], ["back", "Back"], ["side", "Side"], ["inside", "Inside"], ["unassigned", "Unassigned view"]];
export const FLAT_VIEW_LABEL = Object.fromEntries(FLAT_VIEWS) as Record<string, string>;
export const PATTERN_FORMATS = ["DXF-AAMA", "DXF-ASTM", "Gerber", "Lectra", "PLT / HPGL", "PDF", "Other"];
/** File extensions accepted for pattern and marker files. */
export const PATTERN_EXTENSIONS = ["dxf", "aam", "rul", "plt", "hpgl", "hpg", "mdl", "iba", "vet", "mrk", "gbr", "zip", "pdf"];
/** Drawings: SVG, PDF, AI, PNG, JPG (and WebP). */
export const DRAWING_ACCEPT = "image/svg+xml,.svg,application/pdf,.pdf,.ai,application/illustrator,image/png,image/jpeg,image/webp";

export interface CadFile { fileId: string; name: string; mimeType: string; version: number; by: string; at: string; note: string }
/** A numbered callout pin on a detail view; x / y are percentages of the image. step: a Construction row (index), or -1. */
export interface CadPin { n: number; x: number; y: number; note: string; step: number }
/** One placement on a placement drawing: what, where (measured from a stated reference), and the library artwork (linked). */
export interface CadPlacement { what: string; method: string; reference: string; position: string; size: string; artworkId: string; artworkRevision: number }
/** A point code on the POM diagram, matching a Measurements row. */
export interface CadPoint { code: string; x: number; y: number }
/** A drawing made from a CAD Library design: which design (by code, kept across backups) and which revision. */
export interface CadSource { code: string; revision: number; revisionLabel: string; name: string; colourway: string }
export interface CadDrawing {
  id: string; kind: CadKind; view: string; title: string; colour: string; format: string; note: string; main: boolean;
  current: CadFile; history: CadFile[];
  pins: CadPin[]; placements: CadPlacement[]; points: CadPoint[];
  source?: CadSource;
}

const str = (v: unknown, max = 500) => String(v ?? "").slice(0, max);
const pct = (v: unknown) => Math.max(0, Math.min(100, Math.round((Number(v) || 0) * 10) / 10));
const idRe = /^[0-9a-f-]{36}$/i;
const fileOf = (f: any): CadFile | null => (f && idRe.test(String(f.fileId || "")) ? {
  fileId: String(f.fileId), name: str(f.name, 200), mimeType: str(f.mimeType, 80), version: Math.max(1, Math.floor(Number(f.version) || 1)),
  by: str(f.by, 120), at: str(f.at, 40), note: str(f.note, 500),
} : null);
let seq = 0;
const newId = () => `cad-${Date.now().toString(36)}-${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Tidy the drawings a screen or an import sends. Legacy attachments (the old "Sketches" file ids) that no drawing
 *  holds become technical flats with an unassigned view, so nothing is lost. Only one drawing is the main image. */
export function normalizeCad(v: unknown, legacyAttachments: string[] = []): CadDrawing[] {
  const kinds = new Set(CAD_KINDS.map(([k]) => k));
  const out: CadDrawing[] = [];
  const ids = new Set<string>();
  for (const d of Array.isArray(v) ? v : []) {
    const current = fileOf(d?.current);
    if (!current || !kinds.has(d?.kind)) continue;
    let id = str(d?.id, 60).trim();
    if (!/^[\w-]{1,60}$/.test(id) || ids.has(id)) id = newId();
    ids.add(id);
    const kind = d.kind as CadKind;
    out.push({
      id, kind, view: kind === "flat" ? (FLAT_VIEW_LABEL[d?.view] ? d.view : "unassigned") : str(d?.view, 60), title: str(d?.title, 200), colour: kind === "colourway" ? str(d?.colour, 120) : "",
      format: kind === "pattern" ? (PATTERN_FORMATS.includes(d?.format) ? d.format : "Other") : "", note: str(d?.note, 1000), main: kind !== "pattern" && !!d?.main,
      current, history: (Array.isArray(d?.history) ? d.history : []).map(fileOf).filter((x: CadFile | null): x is CadFile => !!x).slice(-50),
      pins: kind === "detail" ? (Array.isArray(d?.pins) ? d.pins : []).slice(0, 60).map((p: any, i: number) => ({ n: i + 1, x: pct(p?.x), y: pct(p?.y), note: str(p?.note, 300), step: Number.isInteger(p?.step) && p.step >= 0 ? p.step : -1 })) : [],
      placements: kind === "placement" ? (Array.isArray(d?.placements) ? d.placements : []).slice(0, 60).map((p: any) => ({
        what: str(p?.what, 200), method: str(p?.method, 120), reference: str(p?.reference, 200), position: str(p?.position, 200), size: str(p?.size, 120),
        artworkId: str(p?.artworkId, 60), artworkRevision: Math.max(0, Math.floor(Number(p?.artworkRevision) || 0)),
      })).filter((p: CadPlacement) => p.what || p.position || p.artworkId) : [],
      points: kind === "pom" ? (Array.isArray(d?.points) ? d.points : []).slice(0, 120).map((p: any) => ({ code: str(p?.code, 40).trim(), x: pct(p?.x), y: pct(p?.y) })).filter((p: CadPoint) => p.code) : [],
      ...(d?.source && /^CAD-\d{1,8}$/.test(String(d.source.code || "")) ? { source: {
        code: String(d.source.code), revision: Math.max(0, Math.floor(Number(d.source.revision) || 0)), revisionLabel: str(d.source.revisionLabel, 8), name: str(d.source.name, 160), colourway: str(d.source.colourway, 80),
      } } : {}),
    });
  }
  const held = new Set(out.flatMap((d) => [d.current.fileId, ...d.history.map((h) => h.fileId)]));
  for (const f of legacyAttachments) {
    if (!idRe.test(f) || held.has(f)) continue;
    held.add(f);
    out.push({ id: `sketch-${f}`, kind: "flat", view: "unassigned", title: "", colour: "", format: "", note: "", main: false, current: { fileId: f, name: "", mimeType: "", version: 1, by: "", at: "", note: "" }, history: [], pins: [], placements: [], points: [] });
  }
  let main = false;
  for (const d of out) { if (d.main && !main) main = true; else d.main = false; }
  return out;
}

/** Every file a Tech Pack's drawings hold, current and earlier versions. */
export const cadFileIds = (cad: CadDrawing[]) => [...new Set(cad.flatMap((d) => [d.current.fileId, ...d.history.map((h) => h.fileId)]))];
/** The main image: the drawing marked "Main image", else the front flat. */
export function cadMainFile(cad: CadDrawing[]): CadFile | null {
  const d = cad.find((x) => x.main) || cad.find((x) => x.kind === "flat" && x.view === "front");
  return d && isPreviewable(d.current.mimeType) ? d.current : null;
}
/** Whether the screen can show a picture of the file (SVG, PNG, JPG, WebP; PDFs show as a document card). */
export const isPreviewable = (mime: string) => /^image\/(png|jpeg|webp|svg\+xml)$/.test(mime);

/** What's missing, for the section's warnings and the approval check. */
export function cadIssues(cad: CadDrawing[], colourways: string[]): { missingFlats: string[]; colourwaysMissing: string[] } {
  const views = new Set(cad.filter((d) => d.kind === "flat").map((d) => d.view));
  const coloured = new Set(cad.filter((d) => d.kind === "colourway").map((d) => d.colour.toLowerCase()));
  return {
    missingFlats: (["front", "back"] as const).filter((v) => !views.has(v)).map((v) => FLAT_VIEW_LABEL[v]),
    colourwaysMissing: colourways.filter((c) => c && !coloured.has(c.toLowerCase())),
  };
}
/** The message when a Tech Pack can't go for approval without its front and back flats. */
export const flatsRequiredMessage = (missing: string[]) =>
  `Add the ${missing.join(" and ").toLowerCase()} technical flat${missing.length > 1 ? "s" : ""} in section 2, CAD & technical drawings, before sending this Tech Pack for approval.`;

/** The Measurements row a POM point code refers to: the row named exactly the code, or starting with it
 *  ("A" → "A Chest ½", "A. Chest", "A - Chest"). -1 when none. */
export function pomRowFor(code: string, poms: string[]): number {
  const c = code.trim().toLowerCase();
  if (!c) return -1;
  const exact = poms.findIndex((p) => p.trim().toLowerCase() === c);
  if (exact >= 0) return exact;
  return poms.findIndex((p) => { const t = p.trim().toLowerCase(); return t.startsWith(c) && /^[\s.\-:)–]/.test(t.slice(c.length)); });
}

/** Replace a drawing's file: the current file moves into its history (never deleted). */
export function replaceCadFile(d: CadDrawing, next: Omit<CadFile, "version">): CadDrawing {
  return { ...d, history: [...d.history, d.current], current: { ...next, version: d.current.version + 1 } };
}
